import { IconFont } from '@/components/IconFont'
import OrbitAvatar from '@/components/OrbitAvatar'
import { projectContext } from '@/layouts/MainLayout'
import { deleteDirectoryOrFile, getFileTree } from '@/services/file/file'
import { FolderOutlined, UploadOutlined } from '@ant-design/icons'
import { useEventTarget, useRequest } from 'ahooks'
import { Breadcrumb, Button, Dropdown, Input, Menu, Modal, Row, Space, Table } from 'antd'
import classnames from 'classnames'
import dayjs from 'dayjs'
import prettyBytes from 'pretty-bytes'
import { memo, useContext, useMemo, useState } from 'react'
import DirectoryName from './DirectoryName'
import styles from './index.module.less'
import MoveFile from './MoveFile'
import NewDirectory from './NewDirectory'
import UploadFile from './UploadFile'

export default memo(function ProjectFiles() {
  const { projectId, projectType } = useContext(projectContext)
  const [curDirId, setCurDirId] = useState(undefined)
  const [filterValue, { onChange: filterValueChange }] = useEventTarget({ initialValue: '' })
  const { data: files = [], refresh: refreshFiles } = useRequest(() => getFileTree({ projectType, projectId }), {
    onSuccess: (data) => {
      if (!curDirId) {
        const target = data.find((item) => !item.dirId)
        setCurDirId(target.id)
      }
    }
  })
  const fileLen = useMemo(() => files.filter((item) => !!item.file)?.length, [files])
  const curDirFile = useMemo(
    () => files.filter((item) => item.dirId === curDirId).sort((a, b) => Number(!!a.file) - Number(!!b.file)),
    [curDirId, files]
  )
  const breadcrumbItems = useMemo(() => {
    const arr: any[] = []
    if (!curDirId) {
      return arr
    }
    const findItem = (dirId) => {
      const target = files.find((item) => item.id === dirId)
      arr.unshift(target)
      if (target.dirId) {
        findItem(target.dirId)
      }
    }
    findItem(curDirId)
    return arr
  }, [curDirId, files])

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Delete',
      content: 'Are you sure you want to delete this?',
      onOk: async () => {
        await deleteDirectoryOrFile(record.id)
        refreshFiles()
      }
    })
  }

  const columns: any[] = [
    {
      title: '',
      width: 28,
      align: 'right',
      className: 'p-0',
      render: (_, record) => (record.file ? '' : <IconFont style={{ fontSize: '16px' }} type='icon-wenjianjia1' />)
    },
    {
      title: 'Name',
      dataIndex: 'name',
      render: (name, record) =>
        record.file ? (
          <a href={record.file.url} target='_blank' rel='noreferrer'>
            {name}
          </a>
        ) : (
          <DirectoryName
            dir={record}
            onClick={() => {
              setCurDirId(record.id)
            }}
            onEditSuccess={() => refreshFiles()}
          />
        )
    },
    {
      title: 'size',
      dataIndex: 'size',
      align: 'center',
      width: 200,
      render: (size) => size && prettyBytes(size)
    },
    {
      title: 'Upload time',
      dataIndex: 'createTime',
      align: 'center',
      width: 150,
      render: (createTime) => createTime && dayjs(createTime).format('MMM D, YYYY')
    },
    {
      title: 'Uploaded by',
      dataIndex: 'createBy',
      align: 'center',
      width: 150,
      render: (createBy) => createBy && <OrbitAvatar avatar={createBy.avatar!} name={createBy.fullName} />
    },
    {
      title: 'operation',
      align: 'center',
      width: 200,
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu
              style={{ width: '100px' }}
              items={[
                {
                  label: (
                    <MoveFile
                      files={files}
                      file={record}
                      trigger={<span>Move</span>}
                      onSuccess={() => {
                        refreshFiles()
                      }}
                    />
                  ),
                  key: 'move'
                },
                {
                  label: <span onClick={() => handleDelete(record)}>Delete</span>,
                  key: 'delete'
                }
              ]}
            />
          }
        >
          <Button icon={<IconFont type='icon-setting' />} />
        </Dropdown>
      )
    }
  ]

  return (
    <div className={classnames('container-xl', 'p-2')}>
      <div>
        <Row justify={'space-between'}>
          <Breadcrumb>
            {breadcrumbItems.length > 1 &&
              breadcrumbItems.map((item) => (
                <Breadcrumb.Item key={item.id}>
                  <a onClick={() => setCurDirId(item.id)}>{item.name}</a>
                </Breadcrumb.Item>
              ))}
          </Breadcrumb>

          <Space>
            <Input
              value={filterValue}
              placeholder='Search'
              className={'mr-1'}
              style={{ width: '200px' }}
              prefix={<IconFont type={'icon-search'} />}
              onChange={filterValueChange}
            />

            <UploadFile
              dirId={curDirId}
              trigger={
                <Button type='primary' icon={<UploadOutlined />}>
                  Upload
                </Button>
              }
              onSuccess={() => {
                refreshFiles()
              }}
            />

            <NewDirectory
              dirId={curDirId}
              trigger={
                <Button type='primary' icon={<FolderOutlined />}>
                  New Directory
                </Button>
              }
              onSuccess={() => {
                refreshFiles()
              }}
            />
          </Space>
        </Row>

        <div className={classnames('mt-2')}>
          <Table
            size='small'
            dataSource={curDirFile}
            columns={columns}
            pagination={false}
            className={styles.table}
            rowKey={(record) => record.id}
            title={() => `There are ${fileLen} files in total`}
          />
        </div>
      </div>
    </div>
  )
})
